import { useEffect } from 'react';

import { useTelemetryViewEvent } from 'common/hooks';
import {
  allItemsInteractionClickMenuImportSelectFile,
  allItemsInteractionClickSelectImages,
  allItemsInteractionClickStartUploading,
  googleSheetPageView,
  importExistingMenuFromVenueDataImportVenueId,
  interactionClickChangePrice,
  interactionClickSetCourierRestrictions,
  listingManagerAiDescriptionInteractionClickGenerateAgain,
  listingManagerAiDescriptionInteractionClickSave,
  listingManagerAiDescriptionView,
  listingManagerItemEditorInteractionClickGenerate,
  listingManagerItemEditorInteractionClickRefine,
  listingManagerItemEditorInteractionClickSave,
  listingManagerItemEditorView,
  listingManagerProductFamilySuggestionInteractionClickAccepted,
  listingManagerProductFamilySuggestionInteractionClickRejected,
  listingManagerProductFamilySuggestionInteractionIgnored,
} from 'core/telemetry/avo/Avo';
import { useNamedView } from 'modules/menu/hooks/telemetryHooks';

import { FilesMatch } from './pages/SharedMenuBulkImageUploadPage/types';
import {
  getAppliedDiscountAmount,
  getAppliedDiscountType,
  getPriceChangeAmount,
  getPriceChangeType,
  isDiscountChangeType,
} from './pages/SharedMenuEditorPage/components/Collections/BulkActions/PriceChange/utils';
import { ValidPriceChangeFormValues } from './pages/SharedMenuEditorPage/components/Collections/BulkActions/PriceChange/validation';

export const useGoogleSheetPageViewTracking = () => {
  useTelemetryViewEvent('googleSheetPageView', {}, durationMs => googleSheetPageView({ durationMs }));
};

export const useBulkImageUploadStatusTracking = (status: 'loading' | 'error' | 'idle' | 'success') => {
  useEffect(() => {
    if (status === 'success' || status === 'error') {
      const importStatus = status === 'success' ? 'Success' : 'Failure';
      allItemsInteractionClickStartUploading({ importStatus, triggeredFromSharedMenu: true });
    }
  }, [status]);
};

export const useBulkImageUploadFileSelectionTracking = (matchingResults: FilesMatch[]) => {
  useEffect(() => {
    if (matchingResults.length > 0) {
      const notMatched = matchingResults.filter(record => record.status === 'failure');
      const errors = notMatched.flatMap(record => record.files.map(({ name }) => name)).join(', ');
      allItemsInteractionClickSelectImages({
        matchingStatus: notMatched.length === 0 ? 'Success' : `Failed: ${errors}`,
        numberOfImagesSelected: matchingResults.length,
        triggeredFromSharedMenu: true,
      });
    }
  }, [matchingResults]);
};

export const trackSelectionBootstrap = (venueId: string) => {
  importExistingMenuFromVenueDataImportVenueId({
    venueIdSelected: [venueId],
  });
};

export const useBulkChangePriceTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  return (formValues: ValidPriceChangeFormValues) => {
    const isDiscountChange = isDiscountChangeType(formValues.type);

    const priceChangingType = isDiscountChange
      ? getAppliedDiscountType(formValues.type)
      : getPriceChangeType(formValues.type);

    const priceChangingAmountDecimal = isDiscountChange
      ? getAppliedDiscountAmount(formValues)
      : getPriceChangeAmount(formValues);

    const priceChangingDirection = priceChangingAmountDecimal.isPositive() ? 'addition' : 'deduction';
    const priceChangingAmount = priceChangingAmountDecimal.toString();

    interactionClickChangePrice({
      view,
      priceChangingType,
      priceChangingAmount,
      priceChangingDirection,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu: true,
    });
  };
};

export const useBulkChangeDeliveryTracking = (selectedItemIds: string[]) => {
  const view = useNamedView();
  return (restrictionSelected: string) =>
    interactionClickSetCourierRestrictions({
      view,
      restrictionSelected,
      selectedItems: selectedItemIds,
      triggeredFromSharedMenu: true,
    });
};

export const trackSelectXmenuJSONForImport = (message: string) =>
  allItemsInteractionClickMenuImportSelectFile({ importStatus: message, triggeredFromSharedMenu: true });

const useViewAiDescriptionsModalTracking = () => {
  useTelemetryViewEvent('listingManagerAiDescriptionView', {}, durationMs =>
    listingManagerAiDescriptionView({ durationMs }),
  );
};

const useViewSidepanel = (offerId?: string) => {
  useTelemetryViewEvent('listingManagerItemEditorView', {}, durationMs =>
    listingManagerItemEditorView({ listingOfferId: offerId, durationMs }),
  );
};

export const trackAiDescriptions = {
  clickGenerate: listingManagerItemEditorInteractionClickGenerate,
  clickGenerateAgain: listingManagerAiDescriptionInteractionClickGenerateAgain,
  clickRefine: listingManagerItemEditorInteractionClickRefine,
  clickSaveDescription: listingManagerAiDescriptionInteractionClickSave,
  viewModal: useViewAiDescriptionsModalTracking,
};

export const trackProductFamilySuggestion = {
  suggestionAccepted: listingManagerProductFamilySuggestionInteractionClickAccepted,
  suggestionRejected: listingManagerProductFamilySuggestionInteractionClickRejected,
  suggestionIgnored: listingManagerProductFamilySuggestionInteractionIgnored,
};

export const trackSidePanel = {
  clickSaveOffer: listingManagerItemEditorInteractionClickSave,
  viewOffer: useViewSidepanel,
};
